import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';

import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation';
import FeedPostComment from 'scenes/FeedPost/components/FeedPostComment';
import HorizontalLoader from 'components/HorizontalLoader';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import FeedPostCommentForm from 'scenes/FeedPost/components/FeedPostCommentForm';

import { useApi } from 'hooks/useApi';
import { Role } from 'helpers/user';
import { ENDPOINT } from 'constants/endpoints';

const FeedPostCommentsBlock = (props, ref) => {
  const { user, post, actions } = props;
  const { name, avatar } = user;
  const { Comments, CommentCount } = post;
  const {
    onAddComment,
    onRemoveComment,
    onClickShowPreviousComments,
  } = actions;

  const textArea = useRef(null);

  const api = useApi();

  const [isCommentFormDisabled, setCommentFormDisabled] = useState(false);

  useImperativeHandle(ref, () => ({
    focus: () => textArea.current.focus(),
  }));

  const handleSendComment = comment => {
    if (Role.isCandidate(user, true)) {
      setCommentFormDisabled(true);
      const { NewsID: newsId } = post;
      api.post(
        ENDPOINT.ACTION.FEED.POST_COMMENT,
        {
          newsId,
          comment,
        },
        {
          handleError: response => {
            setCommentFormDisabled(false);
            ResponseErrorDialog({ response });
          },
          handleSuccess: response => {
            setCommentFormDisabled(false);
            onAddComment(response);
          },
        }
      );
    }
  };

  const onReply = name => {
    textArea.current.value = `${name}, ${textArea.current.value}`;
    textArea.current.focus();
  };

  const [previousCommentsLink, commentList] = useMemo(() => {
    let link = null;
    if (CommentCount > 2 && Comments.length < CommentCount) {
      link = (
        <span
          className="feed-post__comments-link"
          onClick={onClickShowPreviousComments}
        >
          Show previous comments
        </span>
      );
    }

    const comments = Comments ? (
      Comments.map((comment, index) => (
        <FeedPostComment
          user={user}
          comment={comment}
          key={index}
          onClickRemoveHandler={onRemoveComment}
          onClickReplyHandler={onReply}
        />
      ))
    ) : (
      <div className="horizontal-loader__container">
        <HorizontalLoader />
      </div>
    );

    return [link, comments];
  }, [user, Comments, CommentCount]);

  return (
    <>
      <CSSTransition
        in={true}
        appear={true}
        timeout={ANIMATION_TIMEOUT_APPEAR}
        classNames="fade"
      >
        <div className="feed-post__new-comment-wrapper">
          <FeedPostCommentForm
            disabled={isCommentFormDisabled}
            name={name}
            avatar={avatar}
            onSend={handleSendComment}
            ref={textArea}
            isLast={!(CommentCount > 0)}
          />
        </div>
      </CSSTransition>
      {CommentCount > 0 ? (
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <div className="feed-post__comments-wrapper">
            {previousCommentsLink}
            {commentList}
          </div>
        </CSSTransition>
      ) : (
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <div className="feed-post__comments-wrapper feed-post__comments-wrapper--empty" />
        </CSSTransition>
      )}
    </>
  );
};

export default forwardRef(FeedPostCommentsBlock);
